/**
 * Resolve the folder id to use for uploads from current navigation context.
 */

export type FilesViewMode =
  | 'recent'
  | 'favorites'
  | 'shared-with-me'
  | 'my-shared'
  | 'trash'
  | 'images'
  | 'videos'
  | 'documents'
  | 'audio'
  | 'archives'
  | 'code';

const ALL_FILES_VIEWS = new Set<FilesViewMode>([
  'recent',
  'favorites',
  'shared-with-me',
  'my-shared',
  'trash',
  'images',
  'videos',
  'documents',
  'audio',
  'archives',
  'code'
]);

const VIRTUAL_VIEWS = ALL_FILES_VIEWS;

export const ROOT_UPLOAD_ERROR =
  'Cannot upload files in the root directory. Open a folder first.';

export function isVirtualFilesView(filesView: FilesViewMode | null | undefined): boolean {
  return Boolean(filesView && VIRTUAL_VIEWS.has(filesView));
}

export function isUploadDisabledForView(filesView: FilesViewMode | null | undefined): boolean {
  return filesView === 'trash' || isVirtualFilesView(filesView);
}

/** Hint no longer used — uploads to virtual views / root are blocked. */
export function shouldShowUploadDestinationHint(
  _filesView: FilesViewMode | null | undefined
): boolean {
  return false;
}

export function parseFilesViewFromSearch(search: string): FilesViewMode | null {
  const view = new URLSearchParams(search).get('view');
  if (view && ALL_FILES_VIEWS.has(view as FilesViewMode)) {
    return view as FilesViewMode;
  }
  return null;
}

export function parseRouteFolderId(pathname: string): string | undefined {
  const match = pathname.match(/^\/files\/([^/?#]+)/);
  return match ? match[1] : undefined;
}

export interface ResolveUploadFolderIdParams {
  routeFolderId?: string;
  currentFolderId?: string | null;
  filesView?: FilesViewMode | null;
  rootFolderId?: string | null;
  lastBrowsedFolderId?: string | null;
}

/**
 * Returns folder uuid for upload, or null when upload should be blocked.
 * Only allowed inside a real non-root folder.
 */
export function resolveUploadFolderId({
  routeFolderId,
  currentFolderId,
  filesView,
  rootFolderId
}: ResolveUploadFolderIdParams): string | null {
  if (isUploadDisabledForView(filesView)) {
    return null;
  }

  // Inside a routed folder that is not the tenant root
  if (routeFolderId) {
    if (rootFolderId && routeFolderId === rootFolderId) {
      return null;
    }
    return routeFolderId;
  }

  // Virtual views → blocked
  if (isVirtualFilesView(filesView)) {
    return null;
  }

  // Inside a real non-root folder via currentFolder
  if (currentFolderId && rootFolderId && currentFolderId !== rootFolderId) {
    return currentFolderId;
  }

  // Plain /files Home (root) → blocked
  return null;
}

/**
 * Whether the user can upload in the current context (non-root real folder).
 */
export function canUploadInContext(params: ResolveUploadFolderIdParams): boolean {
  return resolveUploadFolderId(params) != null;
}
